import KeywordSpotlight from "@/components/KeywordSpotlight";
import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { getCluster, clusters, buildClusterFaq } from "@/data/clusters";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useSeoPageDraft } from "@/lib/useSeoPageDraft";

export const ClusterDetail = () => {
  const { id } = useParams();
  const data = getCluster(id);
  const faq = data ? buildClusterFaq(data) : [];
  const draft = useSeoPageDraft(data ? `/clusters/${data.id}` : null);
  const heroImage = draft.heroUrl || data?.image;
  const heroAlt = draft.heroAlt || (data ? `${data.name} ${data.material} cluster` : "");

  useSeo({
    title: data
      ? `${data.name} — ${data.material}`
      : "Manufacturing cluster",
    description: data
      ? `${data.name}, ${data.state} — ${data.material}. ${data.detail.moq} · ${data.detail.lead}. Asia Sourcing.`
      : undefined,
    path: data ? `/clusters/${data.id}` : "/clusters",
    image: heroImage,
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Place",
              "@id": `${SITE_URL}/clusters/${data.id}#place`,
              name: `${data.name}, India — ${data.nickname}`,
              description: `${data.material} manufacturing cluster in India. ${data.intro}`,
              geo: {
                "@type": "GeoCoordinates",
                latitude: data.geo.lat,
                longitude: data.geo.lng,
              },
              address: {
                "@type": "PostalAddress",
                addressLocality: data.name,
                addressRegion: data.state,
                addressCountry: "IN",
              },
              image: data.image,
            },
            {
              "@type": "Service",
              name: `Sourcing — ${data.material} (${data.name})`,
              description: data.headline,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: { "@type": "Country", name: "India" },
              category: data.material,
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/clusters/${data.id}#faq`,
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Clusters", path: "/clusters" },
              { name: data.name, path: `/clusters/${data.id}` },
            ]),
          ],
        }
      : null,
    jsonLdId: "cluster-detail-jsonld",
  });

  if (!data) return <Navigate to="/clusters" replace />;

  const idx = clusters.findIndex((c) => c.id === id);
  const prev = clusters[(idx - 1 + clusters.length) % clusters.length];
  const next = clusters[(idx + 1) % clusters.length];

  return (
    <article data-testid="cluster-detail-page">
      {/* HEAD with hero image */}
      <header
        style={{
          position: "relative",
          minHeight: "70svh",
          background: "var(--ink)",
          color: "var(--paper)",
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${heroImage}")`,
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
            filter: "grayscale(.1) brightness(.55) contrast(1.1)",
            transform: "scale(1.06)",
          }}
          data-testid="cluster-hero-image"
          aria-label={heroAlt}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(26,23,20,.4) 0%, rgba(26,23,20,.6) 60%, rgba(26,23,20,.96) 100%), linear-gradient(90deg, rgba(26,23,20,.86) 0%, rgba(26,23,20,.35) 60%, rgba(26,23,20,0) 100%)",
          }}
        />
        <div
          className="wrap"
          style={{
            position: "relative",
            zIndex: 2,
            paddingBlock: "calc(74px + var(--section)) clamp(3rem, 7vw, 6rem)",
            width: "100%",
          }}
        >
          <Reveal>
            <p
              className="eyebrow eyebrow-light"
              data-testid="cluster-eyebrow"
            >
              Craft · {data.state} · {data.nickname}
            </p>
          </Reveal>
          <WordReveal
            as="h1"
            text={`${data.name} · ${data.material}.`.replace(/·/g, " · ")}
            className="display"
            stagger={70}
            testId="cluster-headline"
          />
          <Reveal as="p" delay={200} style={{ maxWidth: "60ch", color: "rgba(239,234,224,.7)", marginTop: "1.4rem", fontSize: "1.1rem" }}>
            {data.headline}
          </Reveal>
        </div>
      </header>

      {/* FACTS strip */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(2.5rem, 5vw, 4rem)",
          borderBottom: "1px solid var(--line)",
        }}
        data-testid="cluster-facts"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {data.facts.map(([k, v]) => (
            <Reveal key={k}>
              <span className="mono" style={{ color: "var(--brass-deep)" }}>
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.2rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTRO + CAPABILITIES */}
      <section className="wrap" style={{ paddingBlock: "var(--section)" }}>
        <div className="cap-grid">
          <Reveal>
            <p className="eyebrow">Overview</p>
            <h2
              className="display"
              style={{
                fontSize: "var(--t-h1)",
                margin: "1rem 0 1.4rem",
                lineHeight: 1.05,
              }}
            >
              {data.headline}
            </h2>
            <p style={{ color: "var(--ink-soft)", maxWidth: "56ch", fontSize: "1.05rem" }}>
              {data.intro}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginTop: "1.4rem" }}>
              {data.products.map((p) => (
                <span
                  key={p}
                  className="mono"
                  style={{
                    border: "1px solid var(--line)",
                    padding: ".45rem .8rem",
                    fontSize: ".68rem",
                    color: "var(--brass-deep)",
                    borderRadius: 2,
                  }}
                >
                  {p}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <p className="eyebrow">Capabilities on the floor</p>
            <ul
              className="cap-list"
              data-testid="cluster-capabilities"
            >
              {data.capabilities.map((c) => (
                <li key={c}>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">
          Source from {data.name}.
        </Reveal>
        <Reveal className="row" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link
            to={`/contact?category=${encodeURIComponent(data.products[0] || "")}`}
            className="btn btn-fill btn-lg btn-glow"
            data-testid="cluster-cta-enquiry"
          >
            <span>Send an enquiry →</span>
          </Link>
          <Link to="/clusters" className="btn btn-ghost">See all clusters</Link>
        </Reveal>
      </section>

      {/* FAQ — JSON-LD already injected via useSeo; visible HTML for users + crawlers */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="cluster-faq"
      >
        <Reveal as="p" className="eyebrow">Frequently asked · {data.name}</Reveal>
        <WordReveal
          as="h2"
          text="Common questions, answered."
          stagger={55}
          className="display"
        />
        <div
          style={{
            marginTop: "2.4rem",
            border: "1px solid var(--line)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {faq.map((f, i) => (
            <Reveal
              key={i}
              as="details"
              data-testid={`cluster-faq-${i}`}
              style={{
                padding: "1.2rem 1.5rem",
                borderBottom: i < faq.length - 1 ? "1px solid var(--line)" : "none",
                background: i % 2 === 0 ? "var(--paper)" : "var(--paper-raised)",
              }}
            >
              <summary
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  color: "var(--ink)",
                  cursor: "pointer",
                  letterSpacing: "-.005em",
                  listStyle: "none",
                  display: "flex",
                  alignItems: "baseline",
                  gap: "1rem",
                }}
              >
                <span
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".7rem",
                    letterSpacing: ".1em",
                    flexShrink: 0,
                  }}
                >
                  / {String(i + 1).padStart(2, "0")}
                </span>
                {f.q}
              </summary>
              <p
                style={{
                  color: "var(--ink-soft)",
                  margin: ".8rem 0 0 2.4rem",
                  lineHeight: 1.6,
                  fontSize: ".98rem",
                }}
              >
                {f.a}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Sprint B · Reciprocal cluster ↔ country linking for furniture + lighting clusters */}
      {(() => {
        const CLUSTER_CATEGORY_MAP = {
          jodhpur: "furniture",
          saharanpur: "furniture",
          moradabad: "lighting",
          firozabad: "lighting",
          aligarh: "lighting",
        };
        const cat = CLUSTER_CATEGORY_MAP[data.id];
        if (!cat) return null;
        const catLabel = cat === "furniture" ? "Furniture" : "Lighting";
        const slug = cat === "furniture" ? "furniture-sourcing" : "lighting-sourcing";
        const COUNTRIES = [
          { label: "United Kingdom", to: `/regions/uk/${slug}` },
          { label: "Germany", to: `/regions/germany/${slug}` },
          { label: "France", to: `/regions/france/${slug}` },
          { label: "Netherlands", to: `/regions/netherlands/${slug}` },
          { label: "Sweden", to: `/regions/sweden/${slug}` },
          { label: "Finland", to: `/regions/finland/${slug}` },
        ];
        return (
          <section
            className="wrap"
            data-testid={`cluster-${data.id}-country-markets`}
            style={{
              paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
              borderTop: "1px solid var(--line)",
            }}
          >
            <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
              {data.name} {catLabel.toLowerCase()} · destination markets
            </Reveal>
            <Reveal
              as="h2"
              className="display"
              style={{
                fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
                margin: "0 0 1rem",
                letterSpacing: "-.01em",
              }}
            >
              {data.name} {catLabel.toLowerCase()} programmes by buyer market
            </Reveal>
            <Reveal
              as="p"
              style={{
                color: "var(--ink-soft)",
                fontSize: "1.02rem",
                lineHeight: 1.7,
                maxWidth: "70ch",
                margin: "0 0 1.6rem",
              }}
            >
              {catLabel} programmes from the {data.name} cluster run into every major buyer
              market. Each destination-country page carries the specific compliance stack, port
              lane and MOQ tier that market's retail and DTC buyers expect.
            </Reveal>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: ".8rem",
              }}
            >
              {COUNTRIES.map((c, i) => (
                <Link
                  key={i}
                  to={c.to}
                  data-testid={`cluster-${data.id}-country-${c.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="btn btn-ghost"
                  style={{ justifyContent: "flex-start" }}
                >
                  {catLabel} sourcing for {c.label} →
                </Link>
              ))}
            </div>
          </section>
        );
      })()}

      {/* PAGINATION */}
      <nav
        className="wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          paddingBlock: "2rem 4rem",
          borderTop: "1px solid var(--line)",
          fontFamily: "var(--mono)",
          fontSize: ".74rem",
          letterSpacing: ".08em",
          textTransform: "uppercase",
        }}
        aria-label="Cluster pagination"
        data-testid="cluster-pagination"
      >
        <Link to={`/clusters/${prev.id}`} className="btn btn-ghost">
          ← {prev.name}
        </Link>
        <Link to={`/clusters/${next.id}`} className="btn btn-ghost">
          {next.name} →
        </Link>
      </nav>
      <KeywordSpotlight type="cluster" id={data.id} />
    </article>
  );
};

export default ClusterDetail;
